/**
 * MoonSwap · Configurador de relojes por capas.
 *
 * Aislado del tema: solo se carga en la sección `ms-configurator`.
 * Reutiliza (sin modificarlos) el evento estándar `CartLinesUpdateEvent` de Horizon
 * para que el cart drawer y el contador del carrito se actualicen. Si esa
 * integración falla, se redirige a /cart tras añadir los productos.
 */

const LAYERS = ['caja', 'correa', 'separador', 'tija'];
const SOURCE = 'ms-configurator';

/** @returns {DOMException} */
const abortError = () => new DOMException('ms-configurator: sin datos de carrito', 'AbortError');

class MsConfigurator extends HTMLElement {
  /** @type {any} */ #config = null;
  /** @type {Map<string, any>} */ #options = new Map();
  /** @type {Record<string, string | null>} */ #state = {};
  /** @type {Record<string, number>} */ #layerTokens = {};
  /** @type {AbortController | null} */ #abort = null;
  /** @type {((cents: number, format: string, currency: string) => string) | null} */ #formatMoney = null;
  #busy = false;
  #statusTimer = 0;

  connectedCallback() {
    const dataEl = this.querySelector('script[data-ms-config]');
    if (!dataEl) return;

    try {
      this.#config = JSON.parse(dataEl.textContent || '{}');
    } catch (error) {
      console.error('[ms-configurator] Datos no válidos', error);
      return;
    }

    for (const option of this.#config.options || []) this.#options.set(option.id, option);

    // Estado inicial: el que ha renderizado Liquid (aria-checked).
    for (const layer of LAYERS) {
      const checked = this.querySelector(`.ms-rail[data-layer="${layer}"] [aria-checked="true"]`);
      const id = checked instanceof HTMLElement ? checked.dataset.msOption || null : null;
      this.#state[layer] = id && this.#options.has(id) ? id : null;
    }

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('pointerover', this.#onIntent, { signal, passive: true });
    this.addEventListener('touchstart', this.#onIntent, { signal, passive: true });
    this.addEventListener('focusin', this.#onIntent, { signal });
    document.addEventListener('shopify:block:select', this.#onEditorBlockSelect, { signal });

    import('@theme/money-formatting')
      .then((mod) => {
        this.#formatMoney = mod.formatMoney;
        this.#renderSummary();
        this.#renderTotal();
      })
      .catch(() => {});

    const missing = (this.#config.options || []).filter((option) => !option.hasImage);
    if (missing.length) {
      console.warn(
        '[ms-configurator] Opciones sin imagen de capa (súbelas a Contenido → Archivos):',
        missing.map((option) => `${option.source} → ${option.file}`)
      );
    }

    for (const layer of LAYERS) this.#updateLayer(layer);
    this.#renderSummary();
    this.#renderTotal();
    this.#renderWarning();
    this.#renderCta();
    this.#centerSelectedInRails();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    clearTimeout(this.#statusTimer);
  }

  /* ---------- Eventos ---------- */

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    const addButton = target.closest('[data-ms-add]');
    if (addButton) {
      this.#addToCart();
      return;
    }

    const option = target.closest('[data-ms-option]');
    if (option instanceof HTMLButtonElement && !option.disabled) {
      this.#select(option.dataset.layer || '', option.dataset.msOption || null);
    }
  };

  /** Navegación de radiogroup con flechas (roving tabindex). @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const current = /** @type {Element} */ (event.target).closest('[data-ms-option]');
    if (!(current instanceof HTMLButtonElement)) return;

    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (!(event.key in keys) && event.key !== 'Home' && event.key !== 'End') return;

    const rail = current.closest('.ms-rail');
    if (!rail) return;
    const buttons = /** @type {HTMLButtonElement[]} */ (
      Array.from(rail.querySelectorAll('[data-ms-option]')).filter((b) => !(/** @type {HTMLButtonElement} */ (b).disabled))
    );
    let index = buttons.indexOf(current);
    if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = buttons.length - 1;
    else index = (index + keys[/** @type {keyof typeof keys} */ (event.key)] + buttons.length) % buttons.length;

    event.preventDefault();
    const next = buttons[index];
    if (!next) return;
    next.focus();
    this.#select(next.dataset.layer || '', next.dataset.msOption || null);
  };

  /** Precarga la capa en cuanto el usuario muestra intención de tocar una opción. @param {Event} event */
  #onIntent = (event) => {
    const option = /** @type {Element} */ (event.target)?.closest?.('[data-ms-option]');
    if (option instanceof HTMLElement && option.dataset.msOption) this.#layerImage(option.dataset.msOption)?.setAttribute('loading', 'eager');
  };

  /** En el editor de temas, al seleccionar un bloque se muestra esa opción. @param {Event} event */
  #onEditorBlockSelect = (event) => {
    const target = /** @type {Element} */ (event.target);
    if (!(target instanceof HTMLElement) || !this.contains(target) || !target.dataset.msOption) return;
    this.#select(target.dataset.layer || '', target.dataset.msOption);
    target.scrollIntoView({ block: 'nearest', inline: 'center' });
  };

  /* ---------- Selección ---------- */

  /**
   * @param {string} layer
   * @param {string | null} optionId - null = "Ninguno"
   */
  #select(layer, optionId) {
    if (!LAYERS.includes(layer)) return;
    const id = optionId && this.#options.has(optionId) ? optionId : null;
    if (this.#state[layer] === id) return;

    this.#state[layer] = id;

    for (const button of this.querySelectorAll(`.ms-rail[data-layer="${layer}"] [data-ms-option]`)) {
      const isSelected = (/** @type {HTMLElement} */ (button).dataset.msOption || null) === id;
      button.setAttribute('aria-checked', String(isSelected));
      button.setAttribute('tabindex', isSelected ? '0' : '-1');
    }

    this.#updateLayer(layer);
    this.#renderSummary();
    this.#renderTotal();
    this.#renderWarning();
    this.#renderCta();
    this.#setStatus('');
  }

  /**
   * Cada opción tiene su propia <img> (URL generada por Shopify en Liquid) dentro del grupo de su capa.
   * Mostrar una opción = hacer visible su <img> y ocultar las demás del MISMO grupo.
   * Las otras capas no se tocan. "Ninguno" oculta todas las imágenes de ese grupo.
   * @param {string} layer
   */
  async #updateLayer(layer) {
    const group = this.querySelector(`[data-ms-group="${layer}"]`);
    if (!group) return;

    const token = (this.#layerTokens[layer] = (this.#layerTokens[layer] || 0) + 1);
    const id = this.#state[layer];
    const next = id ? this.#layerImage(id) : null;

    if (next && !next.complete) {
      next.setAttribute('loading', 'eager');
      // Se espera a que la imagen esté lista para evitar un hueco; como máximo 4 s.
      await Promise.race([next.decode().catch(() => {}), new Promise((resolve) => setTimeout(resolve, 4000))]);
      if (token !== this.#layerTokens[layer]) return; // Ya se ha elegido otra opción en esta capa.
    }

    // Nunca se muestra una imagen rota: si no ha cargado, la capa queda vacía.
    const visible = next && next.complete && next.naturalWidth === 0 ? null : next;
    if (next && !visible) console.warn('[ms-configurator] No se pudo cargar la capa', next.dataset.msFile, next.currentSrc);

    for (const img of group.querySelectorAll('img[data-ms-layer-option]')) {
      /** @type {HTMLImageElement} */ (img).hidden = img !== visible;
    }
  }

  /** @param {string} optionId @returns {HTMLImageElement | null} */
  #layerImage(optionId) {
    const img = this.querySelector(`img[data-ms-layer-option="${CSS.escape(optionId)}"]`);
    return img instanceof HTMLImageElement ? img : null;
  }

  /** @param {string} layer */
  #selected(layer) {
    const id = this.#state[layer];
    return id ? this.#options.get(id) || null : null;
  }

  #selectedOptions() {
    return LAYERS.map((layer) => this.#selected(layer)).filter(Boolean);
  }

  /* ---------- Render ---------- */

  #renderSummary() {
    for (const layer of LAYERS) {
      const el = this.querySelector(`[data-ms-summary="${layer}"]`);
      if (!el) continue;
      const option = this.#selected(layer);
      el.textContent = option ? this.#money(Number(option.price) || 0) : this.#config.noneLabel;
      el.classList.toggle('is-none', !option);
    }
  }

  #renderTotal() {
    const el = this.querySelector('[data-ms-total]');
    if (!el) return;
    const cents = this.#selectedOptions().reduce((sum, option) => sum + (Number(option.price) || 0), 0);
    el.textContent = this.#money(cents);
  }

  #renderWarning() {
    const el = this.querySelector('[data-ms-warning]');
    if (!(el instanceof HTMLElement)) return;

    const separador = this.#selected('separador');
    const tija = this.#selected('tija');
    const mismatch = Boolean(separador?.movement && tija?.movement && separador.movement !== tija.movement);

    if (mismatch) {
      const names = this.#config.movements || {};
      el.textContent = String(this.#config.text.warning || '')
        .replace('[separador]', names[separador.movement] || separador.movement)
        .replace('[tija]', names[tija.movement] || tija.movement);
    }
    el.hidden = !mismatch;
  }

  #renderCta() {
    const button = this.querySelector('[data-ms-add]');
    if (!(button instanceof HTMLButtonElement)) return;
    const options = this.#selectedOptions();
    const hasCase = Boolean(this.#selected('caja'));
    button.disabled = this.#busy || !hasCase || options.length === 0;
  }

  /** @param {string} message @param {'info' | 'success' | 'error'} [tone] */
  #setStatus(message, tone = 'info') {
    const el = this.querySelector('[data-ms-status]');
    if (!(el instanceof HTMLElement)) return;
    clearTimeout(this.#statusTimer);
    el.textContent = message;
    el.dataset.tone = tone;
    if (message && tone !== 'error') {
      this.#statusTimer = window.setTimeout(() => (el.textContent = ''), 4000);
    }
  }

  #centerSelectedInRails() {
    for (const rail of this.querySelectorAll('.ms-rail')) {
      const selected = rail.querySelector('[aria-checked="true"]');
      if (!(selected instanceof HTMLElement) || !(rail instanceof HTMLElement)) continue;
      const offset = selected.offsetLeft - (rail.clientWidth - selected.offsetWidth) / 2;
      rail.scrollLeft = Math.max(0, offset);
    }
  }

  /** @param {number} cents */
  #money(cents) {
    const { moneyFormat, currency, locale } = this.#config;
    if (this.#formatMoney && moneyFormat) {
      try {
        return this.#formatMoney(cents, moneyFormat, currency);
      } catch (_) {
        // Se usa el formato de respaldo.
      }
    }
    try {
      return new Intl.NumberFormat(locale || undefined, { style: 'currency', currency }).format(cents / 100);
    } catch (_) {
      return (cents / 100).toFixed(2);
    }
  }

  /* ---------- Carrito ---------- */

  async #addToCart() {
    if (this.#busy) return;

    const options = this.#selectedOptions();
    if (!this.#selected('caja') || options.length === 0) return;

    if (options.some((option) => !option.available || !option.variantId)) {
      this.#setStatus(this.#config.text.soldOut, 'error');
      return;
    }

    this.#setBusy(true);
    this.#setStatus(this.#config.text.adding);

    const configId = `MS-${Date.now().toString(36).toUpperCase()}`;
    const items = options.map((option) => ({
      id: Number(option.variantId),
      quantity: 1,
      properties: { _ms_configuracion: configId },
    }));

    // Secciones que Horizon re-renderiza tras añadir (cart drawer / página de carrito).
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    // Integración con el sistema de eventos de Horizon (sin tocar sus archivos).
    /** @type {any} */ let CartLinesUpdateEvent = null;
    /** @type {any} */ let deferred = null;
    try {
      ({ CartLinesUpdateEvent } = await import('@shopify/events'));
      deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
          promise: deferred.promise,
        })
      );
    } catch (error) {
      console.warn('[ms-configurator] Integración con el carrito de Horizon no disponible, se usará /cart', error);
      CartLinesUpdateEvent = null;
      deferred = null;
    }

    let added = false;
    try {
      const response = await fetch(this.#config.routes.cartAdd, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.status) {
        await this.#resolveCartEvent(CartLinesUpdateEvent, deferred, { didError: true, itemCount: 0 });
        this.#setStatus(data.description || data.message || this.#config.text.error, 'error');
        return;
      }

      added = true;
      const integrated = await this.#resolveCartEvent(CartLinesUpdateEvent, deferred, {
        didError: false,
        itemCount: items.length,
        sections: data.sections,
      });

      const drawer = /** @type {any} */ (document.querySelector('theme-drawer#cart-drawer'));
      if (integrated && drawer && typeof drawer.open === 'function') {
        this.#setStatus(this.#config.text.added, 'success');
        if (!drawer.isOpen) drawer.open();
      } else {
        window.location.href = this.#config.routes.cart;
      }
    } catch (error) {
      console.error('[ms-configurator] Error al añadir al carrito', error);
      deferred?.reject(abortError());
      if (added) {
        window.location.href = this.#config.routes.cart;
        return;
      }
      this.#setStatus(this.#config.text.error, 'error');
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * Completa el evento de Horizon con el carrito real, para que drawer y contador se actualicen.
   * @returns {Promise<boolean>} true si Horizon ha recibido el carrito actualizado.
   */
  async #resolveCartEvent(CartLinesUpdateEvent, deferred, detail) {
    if (!CartLinesUpdateEvent || !deferred) return false;
    try {
      const response = await fetch(`${this.#config.routes.cart}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`cart.js ${response.status}`);
      const cart = await response.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { ...detail, items: cart.items, source: SOURCE, sourceId: SOURCE },
      });
      return true;
    } catch (error) {
      console.warn('[ms-configurator] No se pudo sincronizar el carrito', error);
      deferred.reject(abortError());
      return false;
    }
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    this.toggleAttribute('data-busy', busy);
    const button = this.querySelector('[data-ms-add]');
    if (button) button.setAttribute('aria-busy', String(busy));
    this.#renderCta();
  }
}

if (!customElements.get('ms-configurator')) {
  customElements.define('ms-configurator', MsConfigurator);
}